import { useCallback, createContext, useContext, useEffect, useState } from "react";
import GuiContext from "./guiContext";

const AuthenticationContext = createContext();

// Van komponente: čista funkcija nema šta da traži u telu koje se izvršava
// pri svakom iscrtavanju, a ovako sme da uđe u `useCallback` bez zavisnosti.
const extractPermissionNames = (permission) => {
    const names = [];

    function recurse(node) {
        if (!node) return;
        if (node.name !== "owner") names.push(node.name);
        if (Array.isArray(node.children)) {
            for (const child of node.children) {
                recurse(child);
            }
        }
    }

    recurse(permission);
    return names;
};

export const AuthenticationContextProvider = ({ children }) => {
    const { selected: { selectedAccount } = {} } = useContext(GuiContext);
    const [authKeys, setAuthKeys] = useState(() => {
        const storedAuthKeys = localStorage.getItem('authKeys');
        return storedAuthKeys ? JSON.parse(storedAuthKeys) : {};
    });

    const [permissionsData, setPermissionsData] = useState({});
    const [permissionsList, setPermissionsList] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(false);

    useEffect(() => {
        if (authKeys && Object.keys(authKeys).length > 0) {
            localStorage.setItem('authKeys', JSON.stringify(authKeys));
        } else {
            localStorage.removeItem('authKeys');
        }
    }, [authKeys]);

    const updateAuthKey = (key, status, expiration) => {
        setAuthKeys(prevState => {
            const updatedAuthKeys = { ...prevState };
            const accountKey = selectedAccount || "";

            if (!updatedAuthKeys[accountKey]) {
                updatedAuthKeys[accountKey] = {};
            }

            if (updatedAuthKeys[accountKey][key]) {
                updatedAuthKeys[accountKey][key] = {
                    status: status !== undefined ? status : updatedAuthKeys[accountKey][key].status,
                    expiration: expiration !== undefined ? expiration : updatedAuthKeys[accountKey][key].expiration
                };
            } else {
                updatedAuthKeys[accountKey][key] = { status, expiration };
            }

            return updatedAuthKeys;
        });
    };

    const dropAuthKeys = () => {
        setAuthKeys({});
    };

    // Postojana dok se ključevi i nalog ne promene: drže je dijalozi u
    // `useEffect`-ima, pa bi se inače okidali pri svakom iscrtavanju.
    const isPermissionValid = useCallback((permissionName) => {
        const accountKey = selectedAccount || "";
        const permission = authKeys?.[accountKey]?.[permissionName];
        if (!permission) return false;
        const currentTime = Date.now();
        return Boolean(permission.status && permission.expiration > currentTime);
    }, [authKeys, selectedAccount]);

    // Postojana: `useFetchPermissions` je drži u `useEffect`-u koji puni
    // dozvole pri promeni naloga ili servera.
    const updatePermissionsData = useCallback((data) => {
        if (!data) return;
        setPermissionsData(data);
        setPermissionsList(extractPermissionNames(data?.permissions));
    }, []);

    const getCompositionPermission = (composition) => {
        if (permissionsData) {

            const authority = permissionsData?.permissions?.required_authorities?.find(item => item.composition === composition);

            if (authority) {
                return authority.minimum_required_authority.permission;
            } else {
                return null;
            }
        }
    }

    const valueToShare = {
        getCompositionPermission,
        authKeys,
        updateAuthKey,
        dropAuthKeys,
        isPermissionValid,
        updatePermissionsData,
        permissionsData,
        permissionsList,
        permissionsLoading,
        setPermissionsLoading
    }

    return (
        <AuthenticationContext.Provider value={valueToShare}>
            {children}
        </AuthenticationContext.Provider>
    );
}

export default AuthenticationContext;